import React, { useState, useEffect } from 'react';
import axios from '../../http';
import { Link } from 'react-router-dom';
import ConfirmDialog from '../../components/ConfirmDialog';

const StationTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);

  // Station Builder State
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newName, setNewName] = useState('');
  const [briefingText, setBriefingText] = useState('');
  const [skillSearch, setSkillSearch] = useState('');
  const [stationStructure, setStationStructure] = useState([
    {
      id: 'g_' + Date.now(),
      name: 'Tier 1',
      selection_mode: 'random_one',
      assignment_mode: 'company',
      sets: [{ id: 's_' + Date.now(), name: 'Primary Skills', skills: [] }]
    }
  ]);
  const [activeSetId, setActiveSetId] = useState(null);
  const [draggedSkill, setDraggedSkill] = useState(null);

  const handleDragStart = (e, groupId, setId, skillIndex) => {
    setDraggedSkill({ groupId, setId, skillIndex });
    e.dataTransfer.effectAllowed = 'move';
    setTimeout(() => {
      e.target.style.opacity = '0.4';
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedSkill(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetGroupId, targetSetId, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedSkill) return;

    const { groupId: srcGroup, setId: srcSet, skillIndex: srcIdx } = draggedSkill;

    if (srcGroup === targetGroupId && srcSet === targetSetId && srcIdx === targetIndex) {
      return;
    }

    setStationStructure((prev) => {
      const newStructure = JSON.parse(JSON.stringify(prev));
      const sourceGroup = newStructure.find((g) => g.id === srcGroup);
      const sourceSet = sourceGroup.sets.find((s) => s.id === srcSet);

      if (!sourceGroup || !sourceSet) return prev;

      const [movedSkill] = sourceSet.skills.splice(srcIdx, 1);

      const destGroup = newStructure.find((g) => g.id === targetGroupId);
      const destSet = destGroup.sets.find((s) => s.id === targetSetId);

      if (!destGroup || !destSet) return prev;

      let insertIndex = targetIndex;
      if (srcGroup === targetGroupId && srcSet === targetSetId && srcIdx < targetIndex) {
        // Array shifted after removing source element from an earlier index
        insertIndex -= 1;
      }

      destSet.skills.splice(insertIndex, 0, movedSkill);

      return newStructure;
    });
    setDraggedSkill(null);
  };


  useEffect(() => {
    Promise.all([axios.get('/api/admin/station-templates'), axios.get('/api/admin/skills')])
      .then(([tmplRes, skillsRes]) => {
        setTemplates(tmplRes.data);
        setSkills(skillsRes.data);
        setLoading(false);
      })
      .catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    if (stationStructure.length > 0 && stationStructure[0].sets.length > 0 && !activeSetId) {
      setActiveSetId(stationStructure[0].sets[0].id);
    }
  }, [stationStructure, activeSetId]);

  const resetForm = () => {
    setShowCreate(false);
    setEditingId(null);
    setNewName('');
    setBriefingText('');
    setStationStructure([
      {
        id: 'g_' + Date.now(),
        name: 'Tier 1',
        selection_mode: 'random_one',
        assignment_mode: 'company',
        sets: [{ id: 's_' + Date.now(), name: 'Primary Skills', skills: [] }]
      }
    ]);
  };

  const startEdit = async (template) => {
    try {
      const res = await axios.get(`/api/admin/station-templates/${template.id}/details`);
      const data = res.data;
      setNewName(data.name);
      setBriefingText(data.briefing_text || '');
      setEditingId(data.id);

      const mappedGroups = data.groups.map((g) => ({
        id: 'g_' + g.id,
        name: g.name || 'Tier',
        selection_mode: g.selection_mode,
        assignment_mode: g.assignment_mode || 'company',
        sets: g.sets.map((s) => ({
          id: 's_' + s.id,
          name: s.name || '',
          skills: s.skills.map((sk) => ({
            id: sk.id,
            name: sk.name,
            mode: sk.testing_mode
          }))
        }))
      }));
      setStationStructure(
        mappedGroups.length > 0
          ? mappedGroups
          : [
            {
              id: 'g_' + Date.now(),
              name: 'Tier 1',
              selection_mode: 'all',
              sets: [{ id: 's_' + Date.now(), name: '', skills: [] }]
            }
          ]
      );
      setShowCreate(true);
    } catch (err) {
      alert('Failed to load template details');
    }
  };

  const addGroup = () => {
    const newGroupId = 'g_' + Date.now();
    const newSetId = 's_' + Date.now();
    setStationStructure([
      ...stationStructure,
      {
        id: newGroupId,
        name: `Tier ${stationStructure.length + 1}`,
        selection_mode: 'random_one',
        assignment_mode: 'company',
        sets: [{ id: newSetId, name: 'Primary Skills', skills: [] }]
      }
    ]);
    setActiveSetId(newSetId);
  };

  const removeGroup = (groupId) => {
    const updated = stationStructure.filter((g) => g.id !== groupId);
    setStationStructure(updated);
    if (activeSetId && !updated.some((g) => g.sets.some((s) => s.id === activeSetId))) {
      setActiveSetId(updated[0]?.sets[0]?.id || null);
    }
  };

  const updateSetName = (groupId, setId, name) => {
    setStationStructure(
      stationStructure.map((g) =>
        g.id !== groupId ? g : {
          ...g,
          sets: g.sets.map((s) => s.id === setId ? { ...s, name } : s)
        }
      )
    );
  };

  const addSet = (groupId) => {
    const newSetId = 's_' + Date.now();
    setStationStructure(
      stationStructure.map((g) => {
        if (g.id === groupId) {
          return {
            ...g,
            sets: [...g.sets, { id: newSetId, name: `Alternative ${g.sets.length}`, skills: [] }]
          };
        }
        return g;
      })
    );
    setActiveSetId(newSetId);
  };

  const removeSet = (groupId, setId) => {
    setStationStructure(
      stationStructure.map((g) => {
        if (g.id === groupId) {
          return { ...g, sets: g.sets.filter((s) => s.id !== setId) };
        }
        return g;
      })
    );
    if (activeSetId === setId) setActiveSetId(null);
  };

  const addSkillToActiveSet = (skill) => {
    if (!activeSetId) {
      alert('Please select a Set first');
      return;
    }
    setStationStructure(
      stationStructure.map((g) => ({
        ...g,
        sets: g.sets.map((s) => {
          if (s.id === activeSetId) {
            if (s.skills.some((sk) => sk.id === skill.id)) return s;
            return { ...s, skills: [...s.skills, { ...skill, mode: 'individual' }] };
          }
          return s;
        })
      }))
    );
  };

  const removeSkillFromSet = (groupId, setId, skillId) => {
    setStationStructure(
      stationStructure.map((g) => {
        if (g.id === groupId) {
          return {
            ...g,
            sets: g.sets.map((s) => {
              if (s.id === setId) {
                return { ...s, skills: s.skills.filter((sk) => sk.id !== skillId) };
              }
              return s;
            })
          };
        }
        return g;
      })
    );
  };

  const toggleSkillModeInSet = (groupId, setId, skillId) => {
    setStationStructure(
      stationStructure.map((g) => {
        if (g.id === groupId) {
          return {
            ...g,
            sets: g.sets.map((s) => {
              if (s.id === setId) {
                return {
                  ...s,
                  skills: s.skills.map((sk) =>
                    sk.id === skillId
                      ? { ...sk, mode: sk.mode === 'individual' ? 'team' : 'individual' }
                      : sk
                  )
                };
              }
              return s;
            })
          };
        }
        return g;
      })
    );
  };

  const handleSave = async () => {
    if (!newName) return;
    try {
      let templateId = editingId;
      if (!editingId) {
        const res = await axios.post('/api/admin/station-templates', {
          name: newName,
          briefing_text: briefingText || null,
          testing_mode: 'individual',
          draw_mode: stationStructure.some((g) => g.selection_mode === 'random_one')
            ? 'random'
            : 'fixed'
        });
        templateId = res.data.id;
      } else {
        // Implement update template meta if needed (e.g. name update)
        // await axios.put(`/api/admin/station-templates/${editingId}`, { name: newName });
      }

      await axios.post(`/api/admin/station-templates/${templateId}/skills`, {
        groups: stationStructure.map((g) => ({
          ...g,
          // Strip local prefixes when sending to backend
          id: typeof g.id === 'string' && g.id.startsWith('g_') ? null : g.id,
          sets: g.sets.map((s) => ({
            ...s,
            id: typeof s.id === 'string' && s.id.startsWith('s_') ? null : s.id
          }))
        }))
      });

      alert('Template Saved!');
      window.location.reload();
    } catch (err) {
      alert('Error saving template');
    }
  };

  const handleRemoveTemplate = (templateId, templateName) => {
    setConfirm({
      message: `Are you sure you want to delete the template "${templateName}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
      onConfirm: async () => {
        try {
          await axios.delete(`/api/admin/station-templates/${templateId}`);
          setTemplates((prev) => prev.filter(t => t.id !== templateId));
        } catch (err) {
          console.error('Delete error:', err);
          alert('Error deleting template');
        }
      }
    });
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      <ConfirmDialog confirm={confirm} onCancel={() => setConfirm(null)} />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2rem' }}>
        <h1>Station Templates</h1>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            + New Template
          </button>
          <Link to="/admin/dashboard" className="btn btn-secondary">
            Back
          </Link>
        </div>
      </div>

      {showCreate && (
        <div className="modal-overlay">
          <div
            className="wizard-modal"
            style={{ maxWidth: '1100px', width: '95%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                borderBottom: '1px solid #eee',
                paddingBottom: '1rem',
                marginBottom: '1rem'
              }}
            >
              <h3>{editingId ? 'Edit' : 'Create'} Station Template</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxWidth: '400px' }}>
                <input
                  className="form-input"
                  placeholder="Template Name (e.g., Ropes & Knots)"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
                <textarea
                  className="form-input"
                  placeholder="Candidate briefing text (read aloud by proctor before testing begins)..."
                  rows={3}
                  style={{ resize: 'vertical', fontSize: '0.9rem' }}
                  value={briefingText}
                  onChange={(e) => setBriefingText(e.target.value)}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '2rem', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              {/* Skill Library Sidebar */}
              <div
                style={{
                  width: '300px',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRight: '1px solid #eee',
                  paddingRight: '1rem'
                }}
              >
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ fontSize: '0.8rem', color: '#666', fontWeight: '600' }}>
                    SKILL LIBRARY
                  </label>
                  <input
                    className="form-input"
                    placeholder="Search skills..."
                    value={skillSearch}
                    onChange={(e) => setSkillSearch(e.target.value)}
                    style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}
                  />
                </div>
                <div style={{ flex: 1, overflowY: 'auto' }}>
                  {skills
                    .filter((s) => s.name.toLowerCase().includes(skillSearch.toLowerCase()))
                    .map((skill) => (
                      <div
                        key={skill.id}
                        className="skill-library-item"
                        style={{
                          padding: '0.6rem',
                          border: '1px solid #eee',
                          borderRadius: '8px',
                          marginBottom: '0.5rem',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontSize: '0.9rem',
                          background: 'white',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                        }}
                      >
                        <span style={{ fontWeight: '500' }}>{skill.name}</span>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => addSkillToActiveSet(skill)}
                          style={{ padding: '0.2rem 0.5rem' }}
                        >
                          +
                        </button>
                      </div>
                    ))}
                </div>
              </div>

              {/* Station Builder Main Area */}
              <div style={{ flex: 1, overflowY: 'auto' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem'
                  }}
                >
                  <h4 style={{ margin: 0 }}>Station Structure</h4>
                  <button className="btn btn-sm btn-secondary" onClick={addGroup}>
                    + Add Tier
                  </button>
                </div>

                {stationStructure.map((group, gIdx) => (
                  <div
                    key={group.id}
                    style={{
                      border: '1px solid #e2e8f0',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      marginBottom: '2rem',
                      background: '#f8fafc',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '1rem'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <span
                          style={{
                            background: '#3182ce',
                            color: 'white',
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.8rem',
                            fontWeight: 'bold'
                          }}
                        >
                          {gIdx + 1}
                        </span>
                        <input
                          style={{
                            fontWeight: 'bold',
                            border: 'none',
                            background: 'transparent',
                            fontSize: '1.1rem'
                          }}
                          value={group.name}
                          onChange={(e) =>
                            setStationStructure(
                              stationStructure.map((prevG) =>
                                prevG.id === group.id ? { ...prevG, name: e.target.value } : prevG
                              )
                            )
                          }
                        />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        {/* Assignment mode selector */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', color: '#718096' }}>
                          Path selection applies to:
                          <select
                            value={group.assignment_mode || 'company'}
                            onChange={(e) =>
                              setStationStructure(
                                stationStructure.map((prevG) =>
                                  prevG.id === group.id
                                    ? { ...prevG, assignment_mode: e.target.value }
                                    : prevG
                                )
                              )
                            }
                            style={{ fontSize: '0.8rem', padding: '0.2rem 0.4rem', borderRadius: '4px', border: '1px solid #e2e8f0' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <option value="company">Entire Company</option>
                            <option value="individual">Candidate</option>
                          </select>
                        </label>
                        <button
                          className="btn btn-sm"
                          style={{ background: '#fed7d7', color: '#c53030' }}
                          onClick={() => removeGroup(group.id)}
                        >
                          Delete Tier
                        </button>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '1rem', overflowX: 'auto', padding: '0.5rem 0' }}>
                      {group.sets.map((set, sIdx) => {
                        const isSingle = group.sets.length === 1;
                        return (
                          <div
                            key={set.id}
                            className={`set-card ${activeSetId === set.id ? 'active' : ''}`}
                            onClick={() => setActiveSetId(set.id)}
                            style={{
                              minWidth: '220px',
                              flex: isSingle ? 1 : 'none',
                              background: 'white',
                              borderRadius: '8px',
                              padding: '1rem',
                              border: activeSetId === set.id ? '2px solid #3182ce' : '1px solid #e2e8f0',
                              cursor: 'pointer',
                              position: 'relative'
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '0.8rem'
                              }}
                            >
                              <input
                                style={{
                                  fontWeight: '600',
                                  fontSize: '0.9rem',
                                  border: 'none',
                                  borderBottom: '1px solid #e2e8f0',
                                  background: 'transparent',
                                  flex: 1,
                                  minWidth: 0,
                                  outline: 'none',
                                  cursor: 'text'
                                }}
                                value={set.name}
                                placeholder="Path name..."
                                onChange={(e) => {
                                  e.stopPropagation();
                                  updateSetName(group.id, set.id, e.target.value);
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                              {!isSingle && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeSet(group.id, set.id);
                                  }}
                                  style={{ border: 'none', background: 'transparent', color: '#999', cursor: 'pointer', marginLeft: '0.5rem' }}
                                >
                                  ✕
                                </button>
                              )}
                            </div>

                            <div
                              style={{ minHeight: '40px' }}
                              onDragOver={handleDragOver}
                              onDrop={(e) => handleDrop(e, group.id, set.id, set.skills.length)}
                            >
                              {set.skills.map((sk, skIdx) => (
                                <div
                                  key={sk.id}
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, group.id, set.id, skIdx)}
                                  onDragEnd={handleDragEnd}
                                  onDragOver={handleDragOver}
                                  onDrop={(e) => handleDrop(e, group.id, set.id, skIdx)}
                                  style={{
                                    fontSize: '0.85rem',
                                    padding: '0.4rem',
                                    background: '#f1f5f9',
                                    borderRadius: '4px',
                                    marginBottom: '0.4rem',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '0.2rem',
                                    cursor: 'grab'
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontWeight: '500' }}>{sk.name}</span>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        removeSkillFromSet(group.id, set.id, sk.id);
                                      }}
                                      style={{ border: 'none', background: 'transparent', fontSize: '0.7rem' }}
                                    >
                                      🗑️
                                    </button>
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                    <input
                                      type="checkbox"
                                      checked={sk.mode === 'individual'}
                                      onChange={() => toggleSkillModeInSet(group.id, set.id, sk.id)}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                      Individual
                                    </span>
                                  </div>
                                </div>
                              ))}
                              {set.skills.length === 0 && (
                                <div
                                  style={{
                                    fontSize: '0.8rem',
                                    color: '#cbd5e0',
                                    textAlign: 'center',
                                    padding: '1rem',
                                    border: '1px dashed #e2e8f0',
                                    borderRadius: '4px'
                                  }}
                                >
                                  No Skills
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      <button
                        className="btn btn-sm btn-secondary"
                        style={{ alignSelf: 'center', minWidth: '100px' }}
                        onClick={() => addSet(group.id)}
                      >
                        + Add Alternative Skills
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-actions" style={{ display: 'flex', alignItems: 'center', gap: '1rem', borderTop: '1px solid #eee', paddingTop: '1rem', marginTop: '1rem', paddingBottom: '0.5rem' }}>
              <p
                style={{
                  flex: 1,
                  fontSize: '0.85rem',
                  color: '#718096',
                  textAlign: 'left',
                  margin: 0
                }}
              >
                <b>Tip:</b> Click a set card to select it, then click "+" on library skills to add
                them.
              </p>
              <button className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSave} disabled={!newName}>
                Save Station Template
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          marginTop: '2rem',
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}
      >
        {templates.map((t) => (
          <div
            key={t.id}
            style={{
              padding: '1rem',
              borderBottom: '1px solid #eee',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <strong>{t.name}</strong>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-sm btn-secondary" onClick={() => startEdit(t)}>
                Edit
              </button>
              <button className="btn btn-sm" style={{ background: '#fed7d7', color: '#c53030' }} onClick={() => handleRemoveTemplate(t.id, t.name)}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex; align-items: center; justify-content: center;
          z-index: 1000;
        }
        .wizard-modal {
          background: white;
          padding: 2rem;
          border-radius: 12px;
          box-shadow: 0 10px 25px rgba(0,0,0,0.2);
        }
        .set-card.active {
          box-shadow: 0 0 0 3px rgba(49, 130, 206, 0.2);
        }
        .skill-library-item:hover {
          border-color: #3182ce !important;
          transform: translateY(-1px);
        }
      `}</style>
    </div>
  );
};

export default StationTemplates;
